import React, { useContext, useRef, useState, useEffect } from "react";
import { DirectionalHint, TooltipDelay } from "@fluentui/react";
import { isMobile } from "react-device-detect";
import ViewportContext from "../../../../../context/studyviewer/ViewportContext";
import themeContext from "../../../../../context/theme/ThemeContext";
import FreeRotate from "../../../../../components/SVGComponents/Tools/FreeRotate";
import DropDown from "../../../../../components/SVGComponents/Tools/DropDown";
import { setStackPosition, setRotation, resetRotation, getViewportRotation, FlipHorizontally, FlipVertically } from "../../../helpers/CornerstoneViewportService";
import GetToolSVG from "./GetToolSVG";
import { HP_VIEWPORT_ALIGNMENT } from "../../../../../constants/Constants";
import { useTranslation } from "react-i18next";
import CustomToolTipHost from "../../../../../components/CustomToolTipHost/CustomToolTipHost";
import { toolTipStyles } from "../../../styles/styles.jsx";

const FreeRotateToolComponent = ({ tool }) => {
  const dropDownRef = useRef(null);
  const { theme } = useContext(themeContext);
  const [showDropDown, setShowDropDown] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [dropDownHovered, setDropDownHovered] = useState(false);
  const {
    activeTool,
    setActiveTool,
    setPreviousActiveTool,
    activeViewportIndex,
    handleDefaultToolChange,
    boundingBoxMode,
    setBoundingBoxMode,
    isCropEnable,
    setIsCropEnable,
    alignmentLocks,
    lockViewportAlignment,
    unlockViewportAlignment,
  } = useContext(ViewportContext);
  const { t } = useTranslation();

  const changeActiveTool = () => {
    const isBBMode = boundingBoxMode;
    const isCropMode = isCropEnable;
    setIsCropEnable(false);
    setBoundingBoxMode(false);
    if (!isBBMode && !isCropMode && activeTool === tool.key) {
      setPreviousActiveTool(activeTool);
      handleDefaultToolChange();
    } else {
      setPreviousActiveTool(activeTool);
      setActiveTool(tool.key);
    }
  };

  const handleDropDownClick = () => {
    setShowDropDown((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropDownRef.current && !dropDownRef.current.contains(event.target)) {
        setShowDropDown(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  const [hoveredItem, setHoveredItem] = useState(null);

  const activeLock = alignmentLocks?.get(activeViewportIndex);

  /**
   * Align the active viewport, and settle what happens to any lock on it.
   *
   * A plain Align is the radiologist saying where the image goes now, so it
   * releases a lock rather than fighting one — the lock would otherwise drag the
   * image straight back to the side it was holding. Locking, by contrast, writes
   * the lock and leaves the alignment to the viewport's own lock effect, which
   * re-asserts it and then keeps re-asserting it for every series after this one.
   */
  const handleAlign = async (alignment, lock = false) => {
    setShowDropDown(false);
    if (lock) {
      lockViewportAlignment(activeViewportIndex, alignment);
      return;
    }
    unlockViewportAlignment(activeViewportIndex);
    const currentRotation = await getViewportRotation(activeViewportIndex);
    await setStackPosition(activeViewportIndex, alignment, currentRotation);
  };

  const dropdownItemStyle = (key) => ({
    height: "40px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    cursor: "pointer",
    gap: "10px",
    color: hoveredItem === key ? theme.palette.activeTool : "inherit",
  });

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          height: "100%",
          position: "relative",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <div
          style={{
            height: "100%",
            width: isMobile ? "60px" : "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              height: isMobile ? "65%" : "100%",
              display: "flex",
              alignItems: "center",
            }}
          >
            <div
              className={isMobile ? "highlight-tool-mobile" : "highlight-tool"}
              style={{ width: "45px" }}
            >
              <CustomToolTipHost
                delay={TooltipDelay.zero}
                id="freerotatetool"
                directionalHint={DirectionalHint.rightCenter}
                styles={toolTipStyles}
                content={isMobile ? "" : t("viewer.tools.free-rotate")}
              >
                <div
                  style={{
                    height: "100%",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                  }}
                  onClick={changeActiveTool}
                  onMouseOver={() => setHovered(true)}
                  onMouseLeave={() => setHovered(false)}
                >
                  <FreeRotate
                    color={
                      activeTool === tool.key || hovered
                        ? theme.palette.activeTool
                        : theme.palette.inactiveTool
                    }
                  />
                </div>
              </CustomToolTipHost>
              <div
                style={{
                  marginLeft: "4px",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
                onClick={handleDropDownClick}
                onMouseOver={() => setDropDownHovered(true)}
                onMouseLeave={() => setDropDownHovered(false)}
              >
                <DropDown
                  color={
                    dropDownHovered || hovered
                      ? theme.palette.activeTool
                      : theme.palette.inactiveTool
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDropDown && (
        <div
          className="dropdow-top-position viewer-tool-more-dropdown"
          ref={dropDownRef}
          style={{
            flexDirection: "column",
            position: "absolute",
            zIndex: "999",
            boxShadow: "0px 0.6px 1.8px rgba(0,0,0,0.1), 0px 3.2px 7.2px rgba(0,0,0,0.13)",
            borderRadius: "5px",
            paddingLeft: "10px",
            paddingRight: "20px",
          }}
        >
          {/* Rotate options */}
          <div
            style={dropdownItemStyle("RotateRight")}
            onClick={async () => { await setRotation(activeViewportIndex, 90); setShowDropDown(false); }}
            onMouseEnter={() => setHoveredItem("RotateRight")}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div className="highlight-tool">
              <GetToolSVG toolName="RotateRight" hovered={hoveredItem === "RotateRight"} />
            </div>
            <span>{t("viewer.tools.rotate-right")}</span>
          </div>
          <div
            style={dropdownItemStyle("RotateLeft")}
            onClick={async () => { await setRotation(activeViewportIndex, -90); setShowDropDown(false); }}
            onMouseEnter={() => setHoveredItem("RotateLeft")}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div className="highlight-tool">
              <GetToolSVG toolName="RotateLeft" hovered={hoveredItem === "RotateLeft"} />
            </div>
            <span>{t("viewer.tools.rotate-left")}</span>
          </div>

          {/* Separator */}
          <div style={{ borderTop: "1px solid #3B3A39", margin: "4px 0" }} />

          {/* Flip options */}
          <div
            style={dropdownItemStyle("FlipVertical")}
            onClick={async () => { await FlipVertically(activeViewportIndex); setShowDropDown(false); }}
            onMouseEnter={() => setHoveredItem("FlipVertical")}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div className="highlight-tool">
              <GetToolSVG toolName="FlipVertical" hovered={hoveredItem === "FlipVertical"} />
            </div>
            <span>{t("viewer.tools.flipV")}</span>
          </div>
          <div
            style={dropdownItemStyle("FlipHorizontal")}
            onClick={async () => { await FlipHorizontally(activeViewportIndex); setShowDropDown(false); }}
            onMouseEnter={() => setHoveredItem("FlipHorizontal")}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div className="highlight-tool">
              <GetToolSVG toolName="FlipHorizontal" hovered={hoveredItem === "FlipHorizontal"} />
            </div>
            <span>{t("viewer.tools.flipH")}</span>
          </div>

          {/* Separator */}
          <div style={{ borderTop: "1px solid #3B3A39", margin: "4px 0" }} />

          {/* Alignment options */}
          <div
            style={dropdownItemStyle("AlignLeft")}
            onClick={() => handleAlign(HP_VIEWPORT_ALIGNMENT.LEFT)}
            onMouseEnter={() => setHoveredItem("AlignLeft")}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div className="highlight-tool">
              <GetToolSVG toolName="AlignLeft" hovered={hoveredItem === "AlignLeft"} />
            </div>
            <span>{t("viewer.tools.align-left")}</span>
          </div>
          <div
            style={dropdownItemStyle("AlignCenter")}
            onClick={() => handleAlign(HP_VIEWPORT_ALIGNMENT.CENTER)}
            onMouseEnter={() => setHoveredItem("AlignCenter")}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div className="highlight-tool">
              <GetToolSVG toolName="AlignCenter" hovered={hoveredItem === "AlignCenter"} />
            </div>
            <span>{t("viewer.tools.align-center")}</span>
          </div>
          <div
            style={dropdownItemStyle("AlignRight")}
            onClick={() => handleAlign(HP_VIEWPORT_ALIGNMENT.RIGHT)}
            onMouseEnter={() => setHoveredItem("AlignRight")}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div className="highlight-tool">
              <GetToolSVG toolName="AlignRight" hovered={hoveredItem === "AlignRight"} />
            </div>
            <span>{t("viewer.tools.align-right")}</span>
          </div>

          {/* Separator */}
          <div style={{ borderTop: "1px solid #3B3A39", margin: "4px 0" }} />

          {/* Lock the alignment: the same two positions, held against sideways
              panning and against whatever series is loaded next. Unlocking is on
              the viewport padlock, and offered here too so the menu is not a
              one-way door. */}
          <div
            style={dropdownItemStyle("LockLeft")}
            onClick={() => handleAlign(HP_VIEWPORT_ALIGNMENT.LEFT, true)}
            onMouseEnter={() => setHoveredItem("LockLeft")}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div className="highlight-tool">
              <GetToolSVG toolName="LockLeft" hovered={hoveredItem === "LockLeft"} />
            </div>
            <span>{t("viewer.tools.lock-left")}</span>
          </div>
          <div
            style={dropdownItemStyle("LockRight")}
            onClick={() => handleAlign(HP_VIEWPORT_ALIGNMENT.RIGHT, true)}
            onMouseEnter={() => setHoveredItem("LockRight")}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div className="highlight-tool">
              <GetToolSVG toolName="LockRight" hovered={hoveredItem === "LockRight"} />
            </div>
            <span>{t("viewer.tools.lock-right")}</span>
          </div>
          {activeLock && (
            <div
              style={dropdownItemStyle("Unlock")}
              onClick={() => {
                unlockViewportAlignment(activeViewportIndex);
                setShowDropDown(false);
              }}
              onMouseEnter={() => setHoveredItem("Unlock")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <div className="highlight-tool">
                <GetToolSVG toolName="Unlock" hovered={hoveredItem === "Unlock"} />
              </div>
              <span>{t("viewer.tools.unlock-alignment")}</span>
            </div>
          )}

          {/* Separator */}
          <div style={{ borderTop: "1px solid #3B3A39", margin: "4px 0" }} />

          {/* Clear Rotation */}
          <div
            style={dropdownItemStyle("ClearRotation")}
            onClick={async () => { await resetRotation(activeViewportIndex); setShowDropDown(false); }}
            onMouseEnter={() => setHoveredItem("ClearRotation")}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div className="highlight-tool">
              <GetToolSVG toolName="Reset" hovered={hoveredItem === "ClearRotation"} />
            </div>
            <span>{t("viewer.tools.clear-rotation")}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FreeRotateToolComponent;
